<template>
    <div class="domain-settings">
        <DsModal
            :is-open="showAuthModal"
            size="fullscreen"
            show-back
            @back="closeAuthModal"
        >
            <AuthenticateDomainModal
                v-if="showAuthModal"
                :app-id="appId"
                :domains="domainsDkim"
                :domain="authModalData.domain"
                :environment="environment"
                :is-new-domain="authModalData.isNewDomain"
                :is-add-validation-to-email-domain-verification-enabled="isAddValidationToEmailDomainVerificationEnabled"
                @close="closeAuthModal"
                @retry="retryEmailDomain"
            />
        </DsModal>

        <DsTabset class="settings-tabset">
            <DsTab
                :label="$t('settings.email.emailAuthTabLabel')"
                selected
            >
                <section class="email-domain-settings tab-content-container">
                    <DsFilledButton
                        class="add-email-auth-button"
                        :aria-label="$t('settings.email.addEmailAuthButton')"
                        @click="addEmailDomain"
                    >
                        <template #leading>
                            <DsIcon name="add" />
                        </template>
                        {{ $t('settings.email.addEmailAuthButton') }}
                    </DsFilledButton>

                    <EmailAuthDomainList
                        :domains="domainsDkim"
                        :is-loading="isLoading"
                        @add-domain="addEmailDomain"
                        @authenticate="showEmailDomainAuthenticationModal"
                        @show-status="showEmailAuthRecordsStatusModal"
                        @remove="removeEmailDomain"
                        @retry="retryEmailDomain"
                    />
                </section>
            </DsTab>

            <DsTab :label="$t('settings.email.fromSettingsTabLabel')">
                <DefaultFromAddress
                    class="tab-content-container"
                    :app-id="appId"
                    @connect-domain="addEmailDomain"
                />
            </DsTab>
        </DsTabset>

        <EmailAuthRecordsStatusModal
            :is-open="isEmailAuthRecordsStatusModalOpen"
            :title="$t('settings.email.domainAuthStatus.emailAuthRecordsStatusModalHeader')"
            :app-id="appId"
            :domain="selectedDomain"
            :dns-records="selectedDnsRecords"
            :environment="environment"
            @edit-domain="handleEditDomain"
            @close="closeEmailAuthRecordsStatusModal"
            @retry="retryEmailDomain"
        />
    </div>
</template>

<script>
import AuthenticateDomainModal from '@/components/settings/email/AuthenticateDomainModal';
import EmailAuthRecordsStatusModal from '@/components/settings/email/EmailAuthRecordsStatusModal.vue';
import EmailAuthDomainList from '@/components/settings/email/EmailAuthDomainList';
import DefaultFromAddress from '@/components/settings/email/DefaultFromAddress';
import {
    SUCCESS, ERROR, LOADING, IDLE,
} from '@/components/shared/constants/loadingStatuses.constants';
import { EMAIL_AUTH_TYPE_LEGACY } from '@/components/settings/settings.constants';
import { filterDomains } from '@/components/settings/util/domains.util';
import axios from 'axios';

export default {
    components: {
        AuthenticateDomainModal,
        EmailAuthDomainList,
        EmailAuthRecordsStatusModal,
        DefaultFromAddress,
    },

    props: {
        appId: {
            type: String,
            default: '',
        },
        environment: {
            type: String,
            default: '',
        },
        isAddValidationToEmailDomainVerificationEnabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            status: IDLE,
            error: '',
            domainsDkim: [],
            authModalData: {},
            showAuthModal: false,
            isEmailAuthRecordsStatusModalOpen: false,
            selectedDomain: null,
        };
    },

    created() {
        this.load();
    },

    computed: {
        isLoading() {
            return this.status === LOADING;
        },

        selectedDnsRecords() {
            if (!this.selectedDomain) {
                return [];
            }

            return this.domainsDkim
                .filter((value) => value?.domain === this.selectedDomain.domain);
        },
    },

    methods: {
        async load() {
            this.status = LOADING;
            this.domainsDkim = [];

            const { data } = await axios.get('/app/emailAuthorization/dkimSettings')
                .catch(() => {
                    this.$error({ message: this.$t('email.auth.loadingError') });
                    this.status = ERROR;
                });
            let { data: suggestedData } = await axios.get('/app/emailAuthorization/unconfiguredUserDomains')
                .catch(() => {
                    this.$error({ message: this.$t('email.auth.loadingError') });
                    this.status = ERROR;
                });

            suggestedData = suggestedData.map((domain) => {
                return { ...domain, emailAuthType: EMAIL_AUTH_TYPE_LEGACY, id: 0 };
            });

            this.domainsDkim.push(...data, ...suggestedData);
            this.domainsDkim = filterDomains(this.domainsDkim);

            this.status = SUCCESS;
        },

        addEmailDomain() {
            const domain = {
                dateCreated: null,
                dkimName: null,
                domain: '',
                domainId: '',
                id: 0,
                lastUpdated: null,
                verified: false,
            };

            this.showEmailDomainAuthenticationModal(domain, true);
        },

        showDomainAuthenticationModal(domain, isNewDomain = false) {
            this.authModalData = {
                domain,
                isNewDomain,
            };

            this.showAuthModal = true;
        },

        closeAuthModal() {
            this.showAuthModal = false;
        },

        showEmailDomainAuthenticationModal(domain, isNewDomain = false) {
            this.showDomainAuthenticationModal(domain, isNewDomain);
        },

        showEmailAuthRecordsStatusModal(domain) {
            this.selectedDomain = domain;
            this.isEmailAuthRecordsStatusModalOpen = true;
        },

        closeEmailAuthRecordsStatusModal() {
            this.selectedDomain = null;
            this.isEmailAuthRecordsStatusModalOpen = false;
        },

        async removeEmailDomain(domain) {
            const emailAuthIdsToDelete = this.domainsDkim
                .filter((d) => d.domain === domain.domain)
                .map((d) => `${d.id}`);

            const promises = emailAuthIdsToDelete.map((id) => {
                return axios.post(`/app/emailAuthorization/${id}/deleteDKIMSetting`);
            });

            await Promise.all(promises)
                .then(async () => {
                    await this.load();
                })
                .catch(async () => {
                    this.$error({ message: this.$t('email.auth.domainDeleteError') });
                });
        },

        retryEmailDomain() {
            this.load();
        },

        displayError() {
            this.$error({
                message: `${this.$t('settings.email.retryError')}`,
            });
        },

        handleEditDomain(domain) {
            this.closeEmailAuthRecordsStatusModal();
            this.showDomainAuthenticationModal(domain);
        },
    },
};

</script>

<style lang="scss" rel="stylesheet/scss" type="text/scss" scoped>
.domain-settings {
    button {
        color: $color-paper;

    }
    button.navigation-button {
        --icon-color: #{$color-ink-900};
    }
    button.outline.selected {
        color: $color-paper;
    }
    button.outline {
        color: $color-blue-700;
        --icon-color: #{$color-blue-700};
    }
    div.trailing-slot {
        --icon-color: #{$color-ink-900};
    }
}

.email-domain-settings {
    display: flex;
    flex-direction: column;
}

.add-email-auth-button {
    margin-left: auto;
}

.auth-status-section {
    margin: $spacing-100 0
}

.domain-settings {
    @include container;

    > section {
        max-width: px-to-rem($extra-small-max-width);
    }
}

.domain-settings ::v-deep {
    .settings-tabset.tabset > .tab-content {
        position: inherit;
        left: 0;
        top: 0;
        clear: both;
        visibility: visible;
    }
    .settings-tabset.tabset > div.tab-header > div > ul {
        margin: 0 auto 0 0;
    }
}

.tab-content-container {
    margin-top: $spacing-200;
}

.description {
    margin-top: $spacing-100;
}

.learn {
    margin-bottom: $spacing-200;
    display: block;
}

h4 {
    margin-top: $spacing-300;
}
</style>
